import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import {
  List,
  Pagination,
  RecordContextProvider,
  useListContext,
  useRedirect,
} from "react-admin";
import CustomFilterCustomerMedia from "../../../filter/custom-filter-customer-media";
import ButtonDownloadField from "../../../ui/button-download-field";
import { CustomerMediaPreview } from "../../../ui/customer-media-preview";
import { useQueryParam } from "../../user-admin/media/list";
import { DownloadWarningBanner } from "./show";
import logoWhite from "../../../assets/logo-white.png";


const recordHasPreview = (record) =>
  Boolean(
    (record?.media_class === "Video" && record?.url) ||
    (record?.media_class === "Audio" && record?.url) ||
    (record?.media_class === "Texto" && record?.body)
  );

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "—";


const makeDownloadSx = (compact = false) => ({
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: compact ? 12 : 13,
  px: compact ? 1 : 1.5,
  py: 0,
  height: 30,
  minHeight: 30,
  lineHeight: 1,
  color: "#ffffff",
  background: "linear-gradient(160deg, #f0365f 0%, #c41a43 100%)",
  border: "1px solid rgba(238,35,82,0.30)",
  boxShadow: "0 1px 4px rgba(238,35,82,0.20)",
  letterSpacing: "0.01em",
  "& .MuiSvgIcon-root": { fontSize: 16 },
  "&:hover": {
    background: "linear-gradient(160deg, #ff4d72 0%, #ee2352 100%)",
    boxShadow: "0 3px 10px rgba(238,35,82,0.30)",
  },
  ...(compact && { flex: 1 }),
});

const makePreviewSx = (compact = false) => ({
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 600,
  fontSize: compact ? 12 : 13,
  px: compact ? 1 : 1.25,
  py: 0,
  height: 30,
  minHeight: 30,
  lineHeight: 1,
  color: "#0f172a",
  background: "linear-gradient(160deg, #ffffff 0%, #f8fafc 100%)",
  border: "1px solid rgba(15,23,42,0.12)",
  boxShadow: "0 1px 4px rgba(15,23,42,0.07)",
  letterSpacing: "0.01em",
  "& .MuiSvgIcon-root": { fontSize: 16 },
  "&:hover": {
    background: "linear-gradient(160deg, #f8fafc 0%, #f1f5f9 100%)",
    boxShadow: "0 3px 10px rgba(15,23,42,0.10)",
  },
  "&.Mui-disabled": {
    color: "#cbd5e1",
    background: "#f8fafc",
    border: "1px solid rgba(15,23,42,0.07)",
    boxShadow: "none",
  },
  ...(compact && { flex: 1 }),
});


const ThumbPlaceholder = () => (
  <Box
    sx={{
      width: "100%",
      height: "100%",
      background: "linear-gradient(135deg, #1f2937 0%, #111827 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <Box
      component="img"
      src={logoWhite}
      alt="ItaSat"
      sx={{ width: "50%", objectFit: "contain", opacity: 0.8 }}
    />
  </Box>
);

const PreviewButton = ({ record, compact = false }) =>
  recordHasPreview(record) ? (
    <CustomerMediaPreview />
  ) : (
    <Tooltip title="Pré-visualização não disponível para este tipo de mídia">
      <span>
        <Button
          variant="contained"
          size="small"
          disabled
          startIcon={<VisibilityOutlinedIcon />}
          sx={makePreviewSx(compact)}
        >
          Preview
        </Button>
      </span>
    </Tooltip>
  );

const ClassCategoryChips = ({ record, overlaid = false }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, flexWrap: "wrap" }}>
    {record.media_class && (
      <Chip
        size="small"
        label={record.media_class}
        sx={{
          fontWeight: 600,
          fontSize: overlaid ? 10 : 11,
          height: overlaid ? 20 : 22,
          letterSpacing: "0.02em",
          borderRadius: "5px",
          background: overlaid ? "rgba(15,23,42,0.65)" : "rgba(15,23,42,0.07)",
          color: overlaid ? "#f1f5f9" : "#334155",
          border: "none",
          backdropFilter: overlaid ? "blur(4px)" : "none",
        }}
      />
    )}
    {record.category_media_name && (
      <Chip
        size="small"
        label={record.category_media_name}
        sx={{
          fontWeight: 600,
          fontSize: overlaid ? 10 : 11,
          height: overlaid ? 20 : 22,
          letterSpacing: "0.02em",
          borderRadius: "5px",
          background: overlaid ? "rgba(238,35,82,0.70)" : "rgba(238,35,82,0.10)",
          color: overlaid ? "#fff" : "#c41a43",
          border: overlaid ? "none" : "1px solid rgba(238,35,82,0.20)",
          backdropFilter: overlaid ? "blur(4px)" : "none",
        }}
      />
    )}
    {record.can_download === false && (
      <Chip
        size="small"
        label="Conteúdo Premium"
        sx={{
          fontWeight: 700,
          fontSize: overlaid ? 10 : 11,
          height: overlaid ? 20 : 22,
          letterSpacing: "0.02em",
          borderRadius: "5px",
          background: overlaid ? "rgba(245, 158, 11, 0.90)" : "rgba(245, 158, 11, 0.12)",
          color: overlaid ? "#1f2937" : "#92400e",
          border: overlaid ? "none" : "1px solid rgba(245, 158, 11, 0.35)",
        }}
      />
    )}
  </Box>
);

const Meta = ({ icon: Icon, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    <Icon sx={{ fontSize: 13, color: "#94a3b8" }} />
    <Typography sx={{ fontSize: 12, color: "#94a3b8", fontWeight: 400, whiteSpace: "nowrap" }}>
      {label}
    </Typography>
  </Box>
);


const CardActions = ({ record, compact = false }) => (
  <Box
    onClick={(e) => e.stopPropagation()}
    sx={{ display: "flex", gap: 0.75, ...(compact ? {} : { alignItems: "center" }) }}
  >
    {record.can_download && (
      <ButtonDownloadField
        source="media"
        text="Download"
        buttonSx={makeDownloadSx(compact)}
        disabled={record.can_download === false}
        disabledTooltip="Esta mídia não está incluída no seu plano."
      />
    )}
    <PreviewButton record={record} compact={compact} />
  </Box>
);


const cardSx = (outOfPlan) => ({
  cursor: "pointer",
  borderRadius: "12px",
  border: outOfPlan
    ? "1px solid rgba(212, 175, 55, 0.85)"
    : "1px solid rgba(0,0,0,0.07)",
  background: "#ffffff",
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  overflow: "hidden",
  transition: "box-shadow 0.18s ease, border-color 0.18s ease",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
    borderColor: outOfPlan ? "rgba(212, 175, 55, 1)" : "rgba(238,35,82,0.18)",
  },
});


const ListCard = ({ record }) => {
  const outOfPlan = record?.can_download === false;

  return (
    <RecordContextProvider value={record}>
      <Card elevation={0} sx={cardSx(outOfPlan)}>
        <CardContent sx={{ p: "0 !important" }}>
          <Box sx={{ display: "flex", alignItems: "stretch", height: 120 }}>

            {/* Thumbnail */}
            <Box sx={{ width: 160, minWidth: 160, flexShrink: 0, overflow: "hidden" }}>
              {record.thumbnail_url ? (
                <CardMedia
                  component="img"
                  image={record.thumbnail_url}
                  alt={record.name || "thumbnail"}
                  sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              ) : (
                <ThumbPlaceholder />
              )}
            </Box>

            {/* Body */}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                px: 2,
                py: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 15,
                  color: "#0f172a",
                  lineHeight: 1.3,
                  mb: 0.5,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <Box component="span" sx={{ color: "#94a3b8", fontWeight: 500, mr: 0.6, fontSize: 13 }}>
                  #{record.id}
                </Box>
                {record.name || "—"}
              </Typography>

              <Typography
                title={record.description}
                sx={{
                  fontSize: 13,
                  color: "#64748b",
                  lineHeight: 1.45,
                  mb: 1,
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {record.description || "Sem descrição"}
              </Typography>

              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
                <Meta icon={CalendarTodayOutlinedIcon} label={formatDate(record.created_at)} />
                <Meta icon={DescriptionOutlinedIcon} label={record.type || "—"} />
              </Stack>
            </Box>

            {/* Actions */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                justifyContent: "space-between",
                flexShrink: 0,
                px: 1.5,
                py: 1.5,
              }}
            >
              <ClassCategoryChips record={record} />
              <CardActions record={record} compact={false} />
            </Box>

          </Box>
        </CardContent>
      </Card>
    </RecordContextProvider>
  );
};


const GridCard = ({ record }) => {
  const outOfPlan = record?.can_download === false;

  return (
    <RecordContextProvider value={record}>
      <Card
        elevation={0}
        sx={{
          ...cardSx(outOfPlan),
          display: "flex",
          flexDirection: "column",
          "&:hover": {
            boxShadow: "0 6px 20px rgba(0,0,0,0.09)",
            borderColor: outOfPlan ? "rgba(212, 175, 55, 1)" : "rgba(238,35,82,0.18)",
          },
        }}
      >
        {/* Thumbnail 16:9 */}
        <Box sx={{ width: "100%", paddingTop: "56.25%", position: "relative", flexShrink: 0 }}>
          {record.thumbnail_url ? (
            <CardMedia
              component="img"
              image={record.thumbnail_url}
              alt={record.name || "thumbnail"}
              sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          ) : (
            <Box sx={{ position: "absolute", inset: 0 }}>
              <ThumbPlaceholder />
            </Box>
          )}
          <Box sx={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 0.5 }}>
            <ClassCategoryChips record={record} overlaid />
          </Box>
        </Box>

        {/* Body */}
        <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", p: "12px 14px 14px !important" }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 14,
              color: "#0f172a",
              lineHeight: 1.3,
              mb: 0.4,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <Box component="span" sx={{ color: "#94a3b8", fontWeight: 500, mr: 0.5, fontSize: 11 }}>
              #{record.id}
            </Box>
            {record.name || "—"}
          </Typography>

          <Typography
            title={record.description}
            sx={{
              fontSize: 12,
              color: "#64748b",
              lineHeight: 1.4,
              mb: 1,
              flex: 1,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {record.description || "Sem descrição"}
          </Typography>

          <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
            <Meta icon={CalendarTodayOutlinedIcon} label={formatDate(record.created_at)} />
            <Meta icon={DescriptionOutlinedIcon} label={record.type || "—"} />
          </Stack>

          <CardActions record={record} compact />
        </CardContent>
      </Card>
    </RecordContextProvider>
  );
};

// Alterar entre os dois tipos de visualização (lista e grade)
const ViewToggle = ({ mode, onChange }) => (
  <Box
    sx={{
      display: "inline-flex",
      borderRadius: "9px",
      border: "1px solid rgba(15,23,42,0.10)",
      background: "#f8fafc",
      p: "3px",
      gap: "3px",
    }}
  >
    {[
      { key: "list", Icon: ViewListOutlinedIcon, label: "Visualização em lista" },
      { key: "grid", Icon: GridViewOutlinedIcon, label: "Visualização em grade" },
    ].map(({ key, Icon, label }) => {
      const active = mode === key;
      return (
        <Tooltip key={key} title={label}>
          <IconButton
            size="small"
            onClick={() => onChange(key)}
            sx={{
              width: 30,
              height: 30,
              borderRadius: "6px",
              color: active ? "#ffffff" : "#64748b",
              background: active ? "linear-gradient(160deg, #f0365f 0%, #c41a43 100%)" : "transparent",
              boxShadow: active ? "0 1px 4px rgba(238,35,82,0.25)" : "none",
              transition: "all 0.15s ease",
              "&:hover": {
                background: active
                  ? "linear-gradient(160deg, #ff4d72 0%, #ee2352 100%)"
                  : "rgba(15,23,42,0.06)",
              },
            }}
          >
            <Icon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      );
    })}
  </Box>
);


const CardListContent = ({ mode }) => {
  const { data, isLoading } = useListContext();

  if (isLoading)
    return (
      <Typography sx={{ color: "#94a3b8", fontSize: 14, py: 4, textAlign: "center" }}>
        Carregando...
      </Typography>
    );

  if (!data || data.length === 0)
    return (
      <Typography sx={{ color: "#94a3b8", fontSize: 14, py: 4, textAlign: "center" }}>
        Nenhuma mídia encontrada.
      </Typography>
    );

  const isGrid = mode === "grid";

  return (
    <Grid container spacing={isGrid ? 1.5 : 1.25}>
      {data.map((record) => (
        <Grid item xs={12} sm={isGrid ? 6 : 12} md={isGrid ? 4 : 12} key={record.id}>
          {isGrid ? <GridCard record={record} /> : <ListCard record={record} />}
        </Grid>
      ))}
    </Grid>
  );
};


const CustomerMediaList = () => {
  const media_class = useQueryParam("media_class");
  const [viewMode, setViewMode] = useState("list");

  return (
    <>
      <DownloadWarningBanner />
      <List
        title={media_class ? `Cesta de Produtos (${media_class})` : "Cesta de Produtos"}
        filters={CustomFilterCustomerMedia}
        actions={null}
        pagination={<Pagination />}
        sx={{
          "& .RaList-main": { overflowX: "hidden" },
          "& .RaList-content": {
            px: 1.5,
            pt: 0,
            pb: 1.5,
            boxSizing: "border-box",
            overflowX: "hidden",
          },
          "& .MuiCard-root": {
            boxSizing: "border-box",
            width: "100%",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1.5, py: 1 }}>
          <ViewToggle mode={viewMode} onChange={setViewMode} />
        </Box>

        <CardListContent mode={viewMode} />
      </List>
    </>
  );
};

export default CustomerMediaList;